import {Injector} from "@angular/core"
import {JobState, JobType} from "@generated"
import {
    RunBackendJobOperationJobDetailsFragment as JobData,
    RunBackendJobOperationCreateJobGQL,
    RunBackendJobOperationCancelJobGQL,
    RunBackendJobOperationGetJobDetailsGQL,
    RunBackendJobOperationGetJobStateGQL,
} from "@app/common/models/background-operation/run-backend-job-operation/run-backend-job-operation.generated"
import {BackgroundOperationItem, BackgroundOperationState} from "@app/common/models/background-operation/background-operation-item"
import {CreateJobGraphData} from "@cm/job-nodes/job-nodes"
import {firstValueFrom, Subscription, timer} from "rxjs"
import {mutateThrowingErrors} from "@app/common/helpers/api/mutate"
import {fetchThrowingErrors, fetchIgnoringErrors} from "@app/common/helpers/api/fetch"

export class RunBackendJobOperation extends BackgroundOperationItem {
    private pollSubscription: Subscription | null = null
    private pollingIntervalMs = 3000
    private jobId: string | undefined

    private jobSystemCreateJob: RunBackendJobOperationCreateJobGQL
    private jobSystemCancelJob: RunBackendJobOperationCancelJobGQL
    private jobSystemGetJobState: RunBackendJobOperationGetJobStateGQL
    private jobSystemGetJobDetails: RunBackendJobOperationGetJobDetailsGQL

    constructor(
        private injector: Injector,
        name: string,
        description: string,
        private createJobInput: {jobType: JobType; name: string; graph: CreateJobGraphData; organizationLegacyId: number},
        private onJobCompleted: (jobData: JobData, setResult: (result: {url: string; title: string}) => void) => void,
    ) {
        super(name, description, true)
        this.abort = this.cancelJob.bind(this)

        this.jobSystemCreateJob = this.injector.get(RunBackendJobOperationCreateJobGQL)
        this.jobSystemCancelJob = this.injector.get(RunBackendJobOperationCancelJobGQL)
        this.jobSystemGetJobState = this.injector.get(RunBackendJobOperationGetJobStateGQL)
        this.jobSystemGetJobDetails = this.injector.get(RunBackendJobOperationGetJobDetailsGQL)
    }

    async submitJob() {
        this.state = BackgroundOperationState.Waiting

        const createJobResult = await mutateThrowingErrors(this.jobSystemCreateJob)({input: this.createJobInput})

        this.jobId = createJobResult.createJob.id
        if (this.jobId === undefined) throw new Error("Failed to create job")

        this.pollSubscription = timer(0, this.pollingIntervalMs).subscribe(() => this.pollJobStatus())
    }

    async cancelJob() {
        if (!this.jobId) throw new Error("Job ID is not set")
        void firstValueFrom(this.jobSystemCancelJob.mutate({id: this.jobId}))
        this.cleanupSubscriptions()
        this.progressSubject.error(new Error("Job has been cancelled"))
        this.state = BackgroundOperationState.Cancelled
    }

    async pollJobStatus() {
        if (!this.jobId) throw new Error("Job ID is not set")
        fetchThrowingErrors(this.jobSystemGetJobState)({id: this.jobId}).then((result) => {
            if (!result.job) this.failOperation("Job not found")

            if (result.job.state === JobState.Complete) this.completeOperation()

            if (result.job.state === JobState.Cancelled) this.cancelledExternally()

            if (result.job.state === JobState.Running || result.job.state === JobState.Runnable) {
                this.state = BackgroundOperationState.InProgress
                this.progressSubject.next(-1) //Progress computation in the zip task is a bit imprecise, so we leave it at -1 for now for an infinite spinner
            }

            if ([JobState.Failed].includes(result.job.state)) this.failOperation("Job failed, state: " + result.job.state)
        })
    }

    private async completeOperation(): Promise<void> {
        if (!this.jobId) throw new Error("Job ID is not set")
        const result = await fetchIgnoringErrors(this.jobSystemGetJobDetails)({id: this.jobId})

        this.cleanupSubscriptions()
        this.state = BackgroundOperationState.Completed
        this.progressSubject.complete()

        this.onJobCompleted(result.job, (result) => {
            this.resultUrl = result.url
            this.resultTitle = result.title
        })
    }

    private failOperation(cause: string): void {
        this.cleanupSubscriptions()
        this.errorCause = cause
        this.state = BackgroundOperationState.Error
        this.progressSubject.error(new Error(cause))
    }

    private cancelledExternally(): void {
        this.cleanupSubscriptions()
        this.state = BackgroundOperationState.Cancelled
    }

    private cleanupSubscriptions(): void {
        this.pollSubscription?.unsubscribe()
        this.pollSubscription = null
    }
}
